import { notify } from "@general-backoffice/core";
import type { ReferrerType, User } from "@investhinks/api-client";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";

import { referrerEndpoints } from "../../api";
import getErrorMessage from "../../utils/getErrorMessage";

interface ManagePartnershipModalProps {
  isOpen: boolean;
  toggle: () => void;
  user: User | null;
  onSuccess?: () => void;
}

const PARTNER_TYPES = ["DEFAULT", "AMBASSADOR", "INFLUENCER", "DISTRIBUTOR"] as const;
const isReferrerType = (val: string): val is ReferrerType => PARTNER_TYPES.some((t) => t === val);

const ManagePartnershipModal: React.FC<ManagePartnershipModalProps> = ({ isOpen, toggle, user, onSuccess }) => {
  const { t } = useTranslation("users", { keyPrefix: "managePartnership" });
  const [selectedType, setSelectedType] = useState<ReferrerType>("DEFAULT");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);

  const userId = user?.id;
  const displayName = user?.name || user?.email || `User #${userId}`;
  const currentType = user?.referrerType || "DEFAULT";
  const isBlocked = user?.isBlockedFromAffiliate;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedType(currentType);
      setError(null);
    }
  }, [isOpen, currentType]);

  const handleChangeType = async () => {
    if (!selectedType || selectedType === currentType || !userId) return;

    setIsLoading(true);
    setError(null);

    try {
      await referrerEndpoints.changePartnerType({
        userId,
        referrerType: selectedType,
      });
      notify.success(String(t("successMessage", { type: t(`types.${selectedType}`) })));
      onSuccess?.();
    } catch (err: unknown) {
      console.error("Failed to change partner type:", err);
      setError(getErrorMessage(err, t("errorGeneric")));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleBlock = async () => {
    if (!userId) return;

    setShowBlockConfirm(false);
    setIsLoading(true);
    setError(null);

    try {
      if (isBlocked) {
        await referrerEndpoints.unblockFromAffiliate(userId);
        notify.success(t("unblockSuccess"));
      } else {
        await referrerEndpoints.blockFromAffiliate(userId);
        notify.success(t("blockSuccess"));
      }
      onSuccess?.();
    } catch (err: unknown) {
      console.error("Failed to toggle block:", err);
      setError(getErrorMessage(err, t("errorGeneric")));
    } finally {
      setIsLoading(false);
    }
  };

  const getBadgeColor = (type: string): string => {
    switch (type) {
      case "AMBASSADOR":
        return "info";
      case "INFLUENCER":
        return "primary";
      case "DISTRIBUTOR":
        return "success";
      default:
        return "secondary";
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle} style={styles.modalHeader}>
        <div style={styles.headerContent}>
          <i className="fas fa-user-tag" style={styles.headerIcon} />
          <span>{t("title")}</span>
        </div>
      </ModalHeader>

      <ModalBody style={styles.modalBody}>
        {/* User Info */}
        <div style={styles.userInfo}>
          <div style={styles.userName}>{displayName}</div>
          <Badge color={getBadgeColor(currentType)} style={styles.typeBadge}>
            {t(`types.${currentType}`)}
          </Badge>
          {isBlocked && (
            <Badge color="danger" style={styles.blockedBadge}>
              {t("blocked")}
            </Badge>
          )}
        </div>

        {/* Partner Type Selection */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("partnerType")}</div>
          <Input
            type="select"
            value={selectedType}
            onChange={(e) => {
              const val = e.target.value;
              if (isReferrerType(val)) setSelectedType(val);
            }}
            style={styles.select}
            disabled={isLoading}
          >
            {PARTNER_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`types.${type}`)}
              </option>
            ))}
          </Input>
          <div style={styles.typeDescription}>{t(`typeDescriptions.${selectedType}`)}</div>
        </div>

        {/* Block Status */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("affiliateAccess")}</div>
          <div style={styles.blockCard}>
            <div style={styles.blockStatus}>
              <span style={styles.blockLabel}>{t("status")}:</span>
              <span style={isBlocked ? styles.statusBad : styles.statusGood}>
                {isBlocked ? t("blockedStatus") : t("activeStatus")}
              </span>
            </div>
            {/* Blocking a user from the affiliate program is destructive and
                revenue-affecting, so it asks first, like every other action in
                this modal family. */}
            {showBlockConfirm ? (
              <div className="d-flex align-items-center" style={{ gap: ".5rem" }}>
                <Button color={isBlocked ? "success" : "danger"} size="sm" onClick={handleToggleBlock} disabled={isLoading}>
                  {isLoading && <Spinner size="sm" className="mr-2" />}
                  {isBlocked ? t("confirmUnblock") : t("confirmBlock")}
                </Button>
                <Button color="secondary" size="sm" outline onClick={() => setShowBlockConfirm(false)} disabled={isLoading}>
                  {t("cancel")}
                </Button>
              </div>
            ) : (
              <Button
                color={isBlocked ? "success" : "danger"}
                outline
                size="sm"
                onClick={() => setShowBlockConfirm(true)}
                disabled={isLoading}
              >
                {isBlocked ? (
                  <>
                    <i className="fas fa-unlock mr-2" aria-hidden="true" />
                    {t("unblock")}
                  </>
                ) : (
                  <>
                    <i className="fas fa-ban mr-2" aria-hidden="true" />
                    {t("block")}
                  </>
                )}
              </Button>
            )}
          </div>
          {showBlockConfirm && (
            <div className="text-muted small mt-2" role="alert">
              {isBlocked
                ? t("unblockConfirmDescription", { name: displayName })
                : t("blockConfirmDescription", { name: displayName })}
            </div>
          )}
          <div style={styles.blockDescription}>{isBlocked ? t("blockedDescription") : t("activeDescription")}</div>
        </div>

        {/* Error */}
        {error && (
          <div style={styles.errorBanner}>
            <i className="fas fa-exclamation-circle" style={styles.errorIcon} />
            <span>{error}</span>
          </div>
        )}
      </ModalBody>

      <ModalFooter style={styles.modalFooter}>
        <Button color="secondary" outline onClick={toggle} disabled={isLoading}>
          {t("cancel")}
        </Button>
        <Button
          color="primary"
          onClick={handleChangeType}
          disabled={selectedType === currentType || isLoading}
          style={styles.saveButton}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {t("saving")}
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2" />
              {t("save")}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const styles = {
  modalHeader: {
    borderBottom: "1px solid #e5e7eb",
    padding: "16px 20px",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  headerIcon: {
    color: "#6366f1",
    fontSize: 18,
  },
  modalBody: {
    padding: "20px",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1px solid #e5e7eb",
  },
  userName: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  typeBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "4px 8px",
  },
  blockedBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "4px 8px",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 10,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  select: {
    padding: "10px 12px",
    fontSize: 14,
    borderRadius: 8,
    border: "1px solid #d1d5db",
  },
  typeDescription: {
    marginTop: 8,
    fontSize: 12,
    color: "#6b7280",
    fontStyle: "italic",
  },
  blockCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    padding: 14,
  },
  blockStatus: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  blockLabel: {
    fontSize: 13,
    color: "#6b7280",
  },
  statusGood: {
    fontSize: 13,
    fontWeight: 500,
    color: "#10b981",
  },
  statusBad: {
    fontSize: 13,
    fontWeight: 500,
    color: "#ef4444",
  },
  blockDescription: {
    marginTop: 8,
    fontSize: 12,
    color: "#6b7280",
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    backgroundColor: "#fef2f2",
    borderRadius: 8,
    fontSize: 13,
    color: "#991b1b",
    marginBottom: 20,
  },
  errorIcon: {
    color: "#ef4444",
  },
  modalFooter: {
    borderTop: "1px solid #e5e7eb",
    padding: "14px 20px",
    display: "flex",
    // A minWidth-pinned primary button plus its cancel overflowed a 360px
    // footer once the label was translated.
    flexWrap: "wrap" as const,
    justifyContent: "flex-end",
    gap: 10,
  },
  saveButton: {
    minWidth: 120,
  },
};

export default ManagePartnershipModal;
